import {useState, useEffect, useRef, useLayoutEffect} from 'react';
import {createConfirmedDraftAutosave} from '../shared/confirmedDraftAutosave.js';
import {useAppDraftGuard} from '../app/navigation/index.js';
import {sb} from '../lib/supabase.js';
import {computePigBatchFCR, pigSourceCountKeys} from '../lib/pig.js';
import {
  pigSetFeederFcrCache,
  pigUpdateProcessingTrip,
  pigDeleteProcessingTrip,
  pigAttachBreederToTrip,
  pigDetachBreederFromTrip,
  pigSetTripBreederHangingWeight,
} from '../lib/pigPlannerApi.js';
import type {MutableRefObject} from 'react';
import type {PigDailysForName, PigFeederGroup, PigProcessingTrip, TripSourceSummary} from '../lib/pig.js';

// ── Types ─────────────────────────────────────────────────────────────────

// A weigh_ins row as the source-tracking select narrows it.
export type SentWeighInRow = {
  id?: string | null;
  session_id?: string | null;
  sent_to_trip_id?: string | null;
  weight?: number | string | null;
};

// The processing-trip form owned by PigContext and threaded in. The index
// signature is load-bearing: persistTrip coerces its numeric fields through a
// COMPUTED key, which a closed shape could not express.
export type PigTripForm = {
  date?: string | null;
  pigCount?: number | string | null;
  liveWeights?: string | null;
  hangingWeight?: number | string | null;
  notes?: string | null;
  [field: string]: unknown;
};

// setTimeout resolves to the DOM overload here (the browser project declares no
// node types), so a pending timer id is a number.
export type TimerRef = MutableRefObject<ReturnType<typeof setTimeout> | undefined>;

export type UsePigProcessingTripsDeps = {
  feederGroups: PigFeederGroup[];
  setFeederGroups: (next: PigFeederGroup[]) => void;
  setNotice: (notice: {kind: string; message: string} | null) => void;
  confirmDelete: (message: string, onConfirm: () => void | Promise<void>) => void;
  tripAutoSaveTimer: TimerRef;
  breeders: Parameters<typeof computePigBatchFCR>[2];
  dailysForName: PigDailysForName;
  activeTripBatchId: string | null;
  setActiveTripBatchId: (next: string | null) => void;
  tripForm: PigTripForm;
  setTripForm: (next: PigTripForm) => void;
  editTripId: string | null;
  setEditTripId: (next: string | null) => void;
  onTripsReloaded?: () => void;
};

// `typeof x === 'object'` narrows `unknown` only to `object`, which has no
// readable `.message`. This predicate is that narrowing, written once for the
// two caught-value warnings below.
const isObjectLike = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// parseInt/parseFloat declare a `string` parameter, but these helpers are fed
// raw form and planner values and rely on the runtime coercion they already
// perform. The cast keeps each call exactly as it was.

// Pig.batch processing-trip workflow (CP10 extraction from PigBatchesView).
// Owns the trip-source tracking state (weigh_ins.sent_to_trip_id ->
// weigh_in_sessions.batch_id) and the add/edit/close/delete handlers. The
// PERSISTED trip edit/delete now routes through the transactional SECURITY
// DEFINER RPCs in pigPlannerApi.js (mig 176) instead of direct app_store JSON
// writes; the server preserves fields it is not sent (subAttributions, ad-hoc
// keys) and, for trips with linked weigh-ins, recomputes pigCount/liveWeights
// from the weigh_ins itself. Numeric coercion (hangingWeight), the
// date-required guard, autosave debounce, close flush, delete confirmation,
// the hasLinkedSource display logic, and the fcrCached stamp/clear contract
// via computePigBatchFCR are all unchanged (fcrCached stays a client-owned
// display cache, re-persisted after each RPC + reload).
//
// The processing-trip FORM state stays owned by PigContext and is threaded in
// (activeTripBatchId/tripForm/editTripId + setters). Other deps explicit:
//   feederGroups                  — ppp-feeders-v1 source of truth (fcrCached
//                                   is saved through pig_set_feeder_fcr_cache)
//   setFeederGroups               — state setter used by the post-RPC reload
//   confirmDelete                 — delete-confirmation helper
//   tripAutoSaveTimer             — shared debounce ref (prop)
//   breeders / dailysForName      — inputs to computePigBatchFCR
export function usePigProcessingTrips({
  feederGroups,
  setFeederGroups,
  setNotice,
  confirmDelete,
  tripAutoSaveTimer,
  breeders,
  dailysForName,
  activeTripBatchId,
  setActiveTripBatchId,
  tripForm,
  setTripForm,
  editTripId,
  setEditTripId,
  onTripsReloaded,
}: UsePigProcessingTripsDeps) {
  // Trip source tracking: for each processing trip, which weigh-in session(s)
  // contributed pigs. Pulled from weigh_ins (sent_to_trip_id) + sessions (batch_id).
  const [tripSentWeighins, setTripSentWeighins] = useState<SentWeighInRow[]>([]);
  const [tripSessionBatch, setTripSessionBatch] = useState<Record<string, string>>({}); // session_id -> batch_id
  useEffect(() => {
    (async () => {
      const {data: sent} = await sb
        .from('weigh_ins')
        .select('id, session_id, sent_to_trip_id, weight')
        .not('sent_to_trip_id', 'is', null);
      if (!sent) return;
      setTripSentWeighins(sent);
      const ids = [...new Set(sent.map((e: SentWeighInRow) => e.session_id).filter(Boolean))];
      if (ids.length === 0) return;
      const {data: sess} = await sb.from('weigh_in_sessions').select('id, batch_id').in('id', ids);
      const m: Record<string, string> = {};
      (sess || []).forEach((s: {id: string; batch_id: string}) => {
        m[s.id] = s.batch_id;
      });
      setTripSessionBatch(m);
    })();
  }, []);
  function tripSourceEntries(tripId: string | null | undefined): SentWeighInRow[] {
    if (!tripId) return [];
    return tripSentWeighins
      .filter((e) => e.sent_to_trip_id === tripId)
      .slice()
      .sort((a, b) => String(a.id || '').localeCompare(String(b.id || '')));
  }
  function tripSourceWeights(tripId: string | null | undefined): number[] {
    return tripSourceEntries(tripId)
      .map((e) => parseFloat(e.weight as string) || 0)
      .filter((w) => w > 0);
  }
  function tripSourceCounts(tripId: string | null | undefined): Record<string, number> {
    const counts: Record<string, number> = {};
    tripSourceEntries(tripId).forEach((e) => {
      // String() on the key reproduces exactly what the JavaScript property
      // access already did for a null/undefined session_id.
      const name = tripSessionBatch[String(e.session_id)] || 'Unknown session';
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }
  function tripSourceCountsByKey(tripId: string | null | undefined): Record<string, number> {
    const counts: Record<string, number> = {};
    tripSourceEntries(tripId).forEach((e) => {
      const name = tripSessionBatch[String(e.session_id)] || 'Unknown session';
      pigSourceCountKeys(name).forEach((key) => {
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return counts;
  }
  function tripSourceSummary(tripId: string | null | undefined): TripSourceSummary {
    const weights = tripSourceWeights(tripId);
    const totalLive = weights.reduce((a, b) => a + b, 0);
    const avgLive = weights.length > 0 ? totalLive / weights.length : null;
    return {
      weights,
      count: weights.length,
      totalLive,
      avgLive,
      counts: tripSourceCounts(tripId),
      countsByKey: tripSourceCountsByKey(tripId),
    };
  }

  // Refresh feederGroups from app_store after a server-side RPC mutation and
  // return the fresh array (the SECDEF trip RPCs write ppp-feeders-v1
  // directly; fcrCached below is recomputed on the reloaded row).
  async function reloadFeeders(): Promise<PigFeederGroup[] | null> {
    const {data, error} = await sb.from('app_store').select('data').eq('key', 'ppp-feeders-v1').maybeSingle();
    if (error || !Array.isArray(data?.data)) {
      setNotice({
        kind: 'error',
        message: 'The trip change was saved, but its display could not be refreshed. Please retry the refresh.',
      });
      return null;
    }
    const groups: PigFeederGroup[] = data.data;
    if (groups) {
      setFeederGroups(groups);
      // Refresh the route owner's canonical Processing labels after mutations.
      onTripsReloaded?.();
    }
    return groups;
  }

  // Stamp parent.fcrCached so Transfer-to-Breeding (which reads it from the
  // persisted record) gets the real adjusted-feed / total-live-weight ratio
  // instead of falling back to the 3.5 industry default. fcrCached stays a
  // CLIENT-maintained display cache: recomputed after every trip edit/delete
  // because the numerator (raw feed) and denominator (trip live wt) both
  // change when trips change. If the helper returns null (no valid trips
  // remaining, or rawFeed <= credits), CLEAR the cache so the transfer flow
  // falls back to the default rather than using a stale ratio. The persist
  // writes ONLY this field's change on top of the server-owned trip state.
  // `batchId` admits null because persistTrip forwards activeTripBatchId,
  // which is null whenever no trip form is open; the id comparison below
  // simply matches nothing then, exactly as before.
  async function refreshFcrCached(batchId: string | null, groups: PigFeederGroup[] | null): Promise<boolean> {
    if (!Array.isArray(groups)) return false;
    const nb = groups.map((g) => {
      if (g.id !== batchId) return g;
      const next = {...g};
      const fcr = computePigBatchFCR(next, dailysForName, breeders, {tripSourceSummary});
      if (fcr != null) next.fcrCached = fcr;
      else delete next.fcrCached;
      return next;
    });
    const before = groups.find((g) => g.id === batchId);
    const after = nb.find((g) => g.id === batchId);
    if (!before || !after || before.fcrCached === after.fcrCached) return true;
    // MIGRATION 237: one field, through pig_set_feeder_fcr_cache, instead of
    // upserting the whole feeder document this hook last read. A derived
    // cache, so the server writes no receipt and no Activity for it.
    try {
      await pigSetFeederFcrCache(sb, String(batchId), typeof after.fcrCached === 'number' ? after.fcrCached : null);
      return true;
    } catch (error) {
      console.warn('Feed conversion cache:', error);
      return false;
    }
  }

  const pendingRefresh = useRef<{batchId: string | null; intent: string} | null>(null);
  const lastConfirmedIntent = useRef<string | null>(null);
  const [tripRefreshPending, setTripRefreshPending] = useState(false);
  async function retryTripRefresh() {
    const pending = pendingRefresh.current;
    if (!pending) return true;
    try {
      const groups = await reloadFeeders();
      if (!(await refreshFcrCached(pending.batchId, groups))) throw new Error('Feed conversion cache was not saved.');
      pendingRefresh.current = null;
      setTripRefreshPending(false);
      setNotice(null);
      return true;
    } catch {
      setTripRefreshPending(true);
      setNotice({
        kind: 'error',
        message:
          'Trip Saved. The display refresh or feed conversion cache failed. Retry Refresh to finish; the trip will not be submitted again.',
      });
      return false;
    }
  }
  async function persistTrip(
    batchId: string | null,
    formSnapshot: PigTripForm,
    currentTripId: string | null,
  ): Promise<string | undefined> {
    if (!(await retryTripRefresh())) return;
    if (!formSnapshot.date) return;
    const intent = JSON.stringify([batchId, currentTripId, formSnapshot]);
    if (lastConfirmedIntent.current === intent) return currentTripId || undefined;
    const sourceWeights = tripSourceWeights(currentTripId);
    // Processing trips are actual processor events sourced from sent weigh-in
    // entries. Do not create a NEW processing trip unless it has stamped
    // weigh_in rows. Legacy existing trips can still be edited with their
    // stored pigCount/liveWeights until a source backfill links them.
    if (!currentTripId) return;
    // hasLinkedSource mirrors the server rule: for trips with linked weigh-ins
    // the RPC recomputes pigCount/liveWeights from the weigh_ins itself
    // (authoritative), so explicit values are only passed on the legacy
    // (unlinked) path — where the stored values still win over the form, as
    // before.
    const hasLinkedSource = sourceWeights.length > 0;
    const tripFormBase: PigTripForm = {...formSnapshot};
    ['hangingWeight'].forEach((key) => {
      const v = tripFormBase[key];
      tripFormBase[key] = v === '' || v == null ? 0 : parseFloat(v as string) || 0;
    });
    const tripId = currentTripId;
    const parent = feederGroups.find((g) => g.id === batchId);
    const existing: PigProcessingTrip = parent ? (parent.processingTrips || []).find((t) => t.id === tripId) || {} : {};
    // The persisted mutation is the transactional SECDEF RPC (mig 176): it
    // locks the store row, preserves fields it is not sent (subAttributions,
    // any future ad-hoc keys), and re-syncs the trip's Processing record in
    // the same transaction.
    try {
      await pigUpdateProcessingTrip(sb, {
        groupId: batchId,
        tripId,
        date: tripFormBase.date,
        hangingWeight: tripFormBase.hangingWeight,
        notes: tripFormBase.notes ?? null,
        pigCount: hasLinkedSource ? null : parseInt((existing.pigCount ?? tripFormBase.pigCount) as string) || 0,
        liveWeights: hasLinkedSource ? null : existing.liveWeights || tripFormBase.liveWeights || '',
      });
    } catch (e) {
      // `e` is a caught value, so `unknown`: only an object can carry
      // `.message`, and a non-object contributed nothing to `e.message || e`.
      setNotice({kind: 'error', message: 'Not Saved. ' + String((isObjectLike(e) && e.message) || e)});
      return;
    }
    lastConfirmedIntent.current = intent;
    pendingRefresh.current = {batchId, intent};
    if (!(await retryTripRefresh())) return;
    if (!editTripId) setEditTripId(tripId);
    return tripId;
  }
  const closing = useRef(false);
  const persistRef = useRef(persistTrip);
  persistRef.current = persistTrip;
  const [autosave] = useState(() =>
    createConfirmedDraftAutosave<{batchId: string | null; form: PigTripForm; id: string | null}>({
      debounceMs: 1500,
      validate: ({form, id}) => !!form.date && !!id,
      persist: async ({batchId, form, id}) => !!(await persistRef.current(batchId, form, id)),
      onState: () => {},
    }),
  );
  useLayoutEffect(() => {
    autosave.resume();
    return () => autosave.suspend();
  }, [autosave]);
  useAppDraftGuard({
    ownerKey: 'pig-processing-trip',
    isUnsafeToLeave: () => autosave.isDirty() || !!pendingRefresh.current,
    settleBeforeLeave: async () => (await autosave.flush()) && (await retryTripRefresh()),
  });
  function updTrip(k: string, v: unknown) {
    if (closing.current) return;
    const next: PigTripForm = {...tripForm, [k]: v};
    setTripForm(next);
    autosave.update({batchId: activeTripBatchId, form: next, id: editTripId});
  }
  async function closeTripForm() {
    if (closing.current) return;
    closing.current = true;
    clearTimeout(tripAutoSaveTimer.current);
    if (activeTripBatchId && editTripId) autosave.update({batchId: activeTripBatchId, form: tripForm, id: editTripId});
    const confirmed = await autosave.flush();
    closing.current = false;
    if (!confirmed) return false;
    setTripForm({date: '', pigCount: 0, liveWeights: '', hangingWeight: 0, notes: ''});
    setEditTripId(null);
    setActiveTripBatchId(null);
    return true;
  }

  function deleteTrip(batchId: string, tripId: string) {
    confirmDelete('Delete this processing trip? This cannot be undone.', async () => {
      if (closing.current) return;
      closing.current = true;
      try {
        if (!(await autosave.flush()) || !(await retryTripRefresh())) return;
        // Transactional SECDEF RPC (mig 176): removes the trip, clears any
        // dangling weigh-in sent_to_trip_id/sent_to_group_id stamps in the SAME
        // transaction (the old client path left them dangling), and applies the
        // worked-archive vs empty-remove rule to the trip's Processing record.
        try {
          await pigDeleteProcessingTrip(sb, {groupId: batchId, tripId});
        } catch (e) {
          setNotice({kind: 'error', message: 'Not Deleted. ' + String((isObjectLike(e) && e.message) || e)});
          return;
        }
        pendingRefresh.current = {batchId, intent: JSON.stringify(['delete', batchId, tripId])};
        // The delete is committed. Its refresh is a separate retryable step.
        await retryTripRefresh();
        if (activeTripBatchId === batchId && editTripId === tripId) {
          setActiveTripBatchId(null);
          setEditTripId(null);
        }
        // The trip delete's pig.batch Activity row is written by the 237
        // wrapper in the delete's own transaction.
      } finally {
        closing.current = false;
      }
    });
  }

  // ── Breeding pigs on a trip (migration 210) ───────────────────────────────
  // Attach/detach route through the transactional SECDEF RPCs, which own the
  // single-trip rule and the idempotency. These return the outcome instead of
  // swallowing it: "that animal is already on trip X" is a real answer the
  // operator has to see, not a console warning.
  //
  // Note what is deliberately ABSENT: no refreshFcrCached call. An attached
  // breeder contributes no feeder pig and no live weight, so the FCR inputs
  // (raw feed, transfer credits, trip live weight) are all unchanged. Adding a
  // recompute here would imply otherwise.
  type BreederTripResult = {ok: true; alreadyAttached?: boolean; detached?: boolean} | {ok: false; message: string};

  async function attachBreederToTrip(
    batchId: string,
    tripId: string,
    breederId: string,
    _breederLabel?: string | null,
  ): Promise<BreederTripResult> {
    let res: {already_attached?: boolean} | null = null;
    try {
      res = (await pigAttachBreederToTrip(sb, {groupId: batchId, tripId, breederId})) as {
        already_attached?: boolean;
      } | null;
    } catch (e) {
      const message = String((isObjectLike(e) && e.message) || e).replace(
        /^pigAttachBreederToTrip:\s*(PROCESSING_VALIDATION:\s*)?/,
        '',
      );
      return {ok: false, message};
    }
    await reloadFeeders();
    // Both sides of the association (the breeding pig's stream and the
    // batch's) get their Activity rows from the 237 wrapper, in the attach's
    // own transaction.
    return {ok: true, alreadyAttached: Boolean(res && res.already_attached)};
  }

  async function detachBreederFromTrip(
    batchId: string,
    tripId: string,
    breederId: string,
    _breederLabel?: string | null,
  ): Promise<BreederTripResult> {
    let res: {detached?: boolean} | null = null;
    try {
      res = (await pigDetachBreederFromTrip(sb, {groupId: batchId, tripId, breederId})) as {
        detached?: boolean;
      } | null;
    } catch (e) {
      const message = String((isObjectLike(e) && e.message) || e).replace(
        /^pigDetachBreederFromTrip:\s*(PROCESSING_VALIDATION:\s*)?/,
        '',
      );
      return {ok: false, message};
    }
    await reloadFeeders();
    // The detachment's Activity row (status unchanged) is the 237 wrapper's.
    return {ok: true, detached: Boolean(res && res.detached)};
  }

  // Record one attached breeder's own carcass weight. Deliberately does NOT
  // touch the trip's hangingWeight: that is the feeder-batch total paired with
  // the trip's liveWeights, and adding a breeder's carcass to it would
  // overstate the batch yield.
  async function setBreederHangingWeight(
    batchId: string,
    tripId: string,
    breederId: string,
    hangingWeight: number | null,
  ): Promise<BreederTripResult> {
    try {
      await pigSetTripBreederHangingWeight(sb, {groupId: batchId, tripId, breederId, hangingWeight});
    } catch (e) {
      const message = String((isObjectLike(e) && e.message) || e).replace(
        /^pigSetTripBreederHangingWeight:\s*(PROCESSING_VALIDATION:\s*)?/,
        '',
      );
      return {ok: false, message};
    }
    await reloadFeeders();
    return {ok: true};
  }

  return {
    tripRefreshPending,
    retryTripRefresh,
    tripSourceCounts,
    tripSourceEntries,
    tripSourceWeights,
    tripSourceSummary,
    updTrip,
    closeTripForm,
    deleteTrip,
    attachBreederToTrip,
    detachBreederFromTrip,
    setBreederHangingWeight,
  };
}
